import * as React from 'react';
import { cn } from '@utils';

const DEFAULT_TABLE_CELL_MAX_HEIGHT = '44px';
const DEFAULT_TABLE_CELL_MAX_WIDTH = '200px';

const TablePrimitiveContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => (
	<div className={cn('flex flex-col w-full bg-white sm:p-2 overflow-x-scroll', className)} {...props}>
		{children}
	</div>
);
TablePrimitiveContainer.displayName = 'TablePrimitiveContainer';

interface TablePrimitiveProps extends React.HTMLAttributes<HTMLTableElement> {
	minHeight?: string;
}

const TablePrimitive = React.forwardRef<HTMLTableElement, TablePrimitiveProps>(
	({
		className,
		minHeight,
		...props
	}, ref) => (
		<div
			className={cn('relative h-full w-full')}
			style={{ minHeight }}
		>
			<table ref={ref} className={cn('caption-bottom relative h-full w-full text-sm', className)} {...props} />
		</div>
	)
);
TablePrimitive.displayName = 'TablePrimitive';

const TablePrimitiveRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
	({ className, style, ...props }, ref) => {
		const [isHovered, setIsHovered] = React.useState(false); // Tailwind refuses to apply the hover background to `td`.

		return (
			<tr
				ref={ref}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				style={{
					...style,
					backgroundColor: isHovered ? 'rgb(249, 250, 251)'
						: 'transparent',
					transition: 'background-color 150ms',
				}}
				className={cn(
					'border-b border-borderSeparator',
					'[&_td:first-child]:pl-2 [&_td:last-child]:pr-2 [&_td]:px-1.5',
					'[&_th:first-child]:pl-2 [&_th:last-child]:pr-2 [&_th]:px-1.5',
					'[&_td:has([role=checkbox])]:pr-2',
					'[&_th:has([role=checkbox])]:pr-2',
					className
				)}
				{...props}
			/>
		)
	}
);
TablePrimitiveRow.displayName = 'TablePrimitiveRow';

const TablePrimitiveHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<thead ref={ref} className={cn('sticky top-0 bg-bgPrimary [&>tr]:border-b-0', className)} {...props} />
	)
);
TablePrimitiveHeader.displayName = 'TablePrimitiveHeader';

interface TablePrimitiveHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
	maxWidth?: string;
	overflow?: 'hidden' | 'visible' | 'scroll' | 'auto' | 'inherit' | 'initial' | 'unset';
}

const TablePrimitiveHead = React.forwardRef<HTMLTableCellElement, TablePrimitiveHeadProps>(
	({ className, maxWidth, overflow, style = {}, ...props }, ref) => (
		<th
			ref={ref}
			className={cn(
				'h-4 truncate text-left text-xs font-medium text-primary [&:has([role=checkbox])]:pr-0',
				className
			)}
			style={{
				...style,
				width: style?.width || DEFAULT_TABLE_CELL_MAX_WIDTH,
				maxWidth: maxWidth || DEFAULT_TABLE_CELL_MAX_WIDTH,
				overflow,
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap'
			}}
			{...props}
		/>
	)
);
TablePrimitiveHead.displayName = 'TablePrimitiveHead';

interface TablePrimitiveBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	minHeight?: string;
}

const TablePrimitiveBody = React.forwardRef<HTMLTableSectionElement, TablePrimitiveBodyProps>(
	({ className, minHeight, ...props }, ref) => (
		<tbody
			ref={ref}
			className={cn('[&_tr:last-child]:border-0 h-full w-full', className)}
			style={{ minHeight }}
			{...props}
		/>
	)
);
TablePrimitiveBody.displayName = 'TablePrimitiveBody';

const TablePrimitiveFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tfoot ref={ref} className={cn('bg-muted/50 border-t border-borderSeparator font-medium', className)} {...props} />
	)
);
TablePrimitiveFooter.displayName = 'TablePrimitiveFooter';

interface TablePrimitiveCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
	maxHeight?: string;
	maxWidth?: string;
	overflow?: 'hidden' | 'visible' | 'scroll' | 'auto' | 'inherit' | 'initial' | 'unset';
	stickyRight?: boolean;
}

const TablePrimitiveCell = React.forwardRef<HTMLTableCellElement, TablePrimitiveCellProps>(
	({
		className,
		maxHeight,
		maxWidth,
		overflow,
		stickyRight,
		style,
		...props
	}, ref) => (
		<td
			ref={ref}
			className={cn(
				'py-1 align-middle text-left text-sm text-primary [&:has([role=checkbox])]:pr-0',
				stickyRight && 'sticky right-0',
				className
			)}
			style={{
				...style,
				maxHeight: maxHeight || DEFAULT_TABLE_CELL_MAX_HEIGHT,
				width: style?.width || DEFAULT_TABLE_CELL_MAX_WIDTH,
				maxWidth: maxWidth || DEFAULT_TABLE_CELL_MAX_WIDTH,
				overflow,
				whiteSpace: 'nowrap'
			}}
			{...props}
		/>
	)
);
TablePrimitiveCell.displayName = 'TablePrimitiveCell';

const TablePrimitiveCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
	({ className, ...props }, ref) => (
		<caption ref={ref} className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
	)
);
TablePrimitiveCaption.displayName = 'TablePrimitiveCaption';

export {
	TablePrimitive,
	TablePrimitiveHeader,
	TablePrimitiveBody,
	TablePrimitiveFooter,
	TablePrimitiveHead,
	TablePrimitiveRow,
	TablePrimitiveCell,
	TablePrimitiveCaption,
	TablePrimitiveContainer,
};
